import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import type { AnalyticsEventDTO } from "@mydailydirect/api-client";

const ANON_KEY = "mdd_anon_id";
const SESS_KEY = "mdd_sess_id";

function getOrCreateId(storage: Storage, key: string, prefix: string): string {
  try {
    let id = storage.getItem(key);
    if (!id) {
      id = `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      storage.setItem(key, id);
    }
    return id;
  } catch (e) {
    return `${prefix}_fallback_${Date.now()}`;
  }
}

export function getAnonymousId(): string {
  if (typeof window === "undefined") return "server";
  return getOrCreateId(window.localStorage, ANON_KEY, "anon");
}

export function getSessionId(): string {
  if (typeof window === "undefined") return "server";
  return getOrCreateId(window.sessionStorage, SESS_KEY, "sess");
}

export function getDeviceType(): string {
  if (typeof window === "undefined") return "desktop";
  const ua = navigator.userAgent;
  if (/mobile/i.test(ua)) return "mobile";
  if (/ipad|tablet/i.test(ua)) return "tablet";
  return "desktop";
}

export function getUtmParams(): { utmSource?: string; utmMedium?: string; utmCampaign?: string } {
  if (typeof window === "undefined") return {};
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      utmSource: params.get("utm_source") || undefined,
      utmMedium: params.get("utm_medium") || undefined,
      utmCampaign: params.get("utm_campaign") || undefined,
    };
  } catch (e) {
    return {};
  }
}

export interface TrackOptions {
  appName?: string;
  userId?: string | null;
  retailerId?: number | null;
  vendorId?: number | null;
  pagePath?: string;
  referrer?: string;
  skuId?: number | null;
  orderId?: number | null;
  searchQuery?: string | null;
  valuePaise?: number | null;
  propsJson?: Record<string, any> | string;
}

export const TELEMETRY_STORAGE_KEY = "mdd_telemetry_events";

export function getLocalTelemetryEvents(): AnalyticsEventDTO[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(TELEMETRY_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveLocalTelemetryEvent(event: AnalyticsEventDTO) {
  if (typeof window === "undefined") return;
  try {
    const list = getLocalTelemetryEvents();
    list.unshift(event);
    if (list.length > 300) list.length = 300;
    localStorage.setItem(TELEMETRY_STORAGE_KEY, JSON.stringify(list));
  } catch {
    // Ignore storage quota errors
  }
}

function resolveSessionUser(): { userId?: string; retailerId?: number; vendorId?: number } {
  if (typeof window === "undefined") return {};
  try {
    for (const key of [
      "mydailydirect.session.v3",
      "mydailydirect.session",
      "mdd_auth_session",
      "mdd_session",
      "trado_auth_session",
    ]) {
      const raw = localStorage.getItem(key) || sessionStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.userId || parsed.user?.userId) {
          const rId = parsed.retailerId ?? (parsed.orgId?.startsWith("retailer_") ? Number(parsed.orgId.replace("retailer_", "")) : undefined);
          const vId = parsed.vendorId ?? (parsed.orgId?.startsWith("vendor_") ? Number(parsed.orgId.replace("vendor_", "")) : undefined);
          return {
            userId: parsed.userId || parsed.user?.userId,
            retailerId: isNaN(rId as any) ? undefined : rId,
            vendorId: isNaN(vId as any) ? undefined : vId,
          };
        }
      }
    }
  } catch {}
  return {};
}

/** Fire-and-forget client-side event tracking. Safe against network failures. */
export function track(eventName: string, options: TrackOptions = {}) {
  if (typeof window === "undefined") return;

  try {
    const utms = getUtmParams();
    const sessionUser = resolveSessionUser();
    const eventPayload: AnalyticsEventDTO = {
      eventName,
      appName: options.appName || "catalog",
      sessionId: getSessionId(),
      anonymousId: getAnonymousId(),
      userId: options.userId ?? sessionUser.userId ?? null,
      retailerId: options.retailerId ?? sessionUser.retailerId ?? null,
      vendorId: options.vendorId ?? sessionUser.vendorId ?? null,
      pagePath: options.pagePath || window.location.pathname,
      referrer: options.referrer || document.referrer || undefined,
      utmSource: utms.utmSource,
      utmMedium: utms.utmMedium,
      utmCampaign: utms.utmCampaign,
      deviceType: getDeviceType(),
      skuId: options.skuId || null,
      orderId: options.orderId || null,
      searchQuery: options.searchQuery || null,
      valuePaise: options.valuePaise || null,
      propsJson: typeof options.propsJson === "object" ? JSON.stringify(options.propsJson) : options.propsJson || null,
      occurredAt: new Date().toISOString(),
    };

    // 1. Always record in local storage audit log
    saveLocalTelemetryEvent(eventPayload);

    // 2. Notify local subscribers within this window
    window.dispatchEvent(new CustomEvent("mdd:telemetry", { detail: eventPayload }));

    // 3. Send to backend endpoint
    const endpoint = "/api/trado/api/v1/analytics/events";
    const bodyStr = JSON.stringify(eventPayload);

    if (typeof fetch === "function") {
      fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: bodyStr,
        keepalive: true,
      }).catch(() => {
        if (navigator.sendBeacon) {
          const blob = new Blob([bodyStr], { type: "application/json" });
          navigator.sendBeacon(endpoint, blob);
        }
      });
    } else if (navigator.sendBeacon) {
      const blob = new Blob([bodyStr], { type: "application/json" });
      navigator.sendBeacon(endpoint, blob);
    }
  } catch (e) {
    // Fail silent
  }
}

/** Hook to automatically track page navigation on route changes. */
export function usePageTracking(appName: string = "catalog") {
  const location = useLocation();
  const lastPathRef = useRef<string>("");

  useEffect(() => {
    const fullPath = location.pathname + location.search;
    if (fullPath !== lastPathRef.current) {
      lastPathRef.current = fullPath;
      track("page_view", {
        appName,
        pagePath: fullPath,
      });
    }
  }, [location, appName]);
}
